// ============================================================================
// FSA STATEMENT HOOKS  (FSA-2.2 Wave 3)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { fsaStatementsService } from 'api/services/fsa/fsaStatementsService';
import type { StatementHydrate, StatementListItem } from 'api/types/fsa/statementData.types';
import type { ID } from 'api/types';

/**
 * List of Statements (BS / PL / CF) for the engagement — drives the
 * workspace left-menu badges (saved-state pill, last-updated, etc.).
 */
export const useFsaStatementList = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<StatementListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) { setData([]); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaStatementsService.list(engagementId);
      setData(r.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};

/**
 * Hydrate one Statement — layout JSON + saved row + live evaluation
 * against current Notes. Frontend Statement screen walks the layout
 * and overlays values from `workspace.data.lines` when saved, falling
 * back to `liveEvaluation` for never-saved Statements.
 */
export const useFsaStatementHydrate = (
  engagementId: ID | null | undefined,
  reportId: ID | null | undefined,
) => {
  const [data, setData] = useState<StatementHydrate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async (): Promise<StatementHydrate | null> => {
    if (!engagementId || !reportId) { setData(null); return null; }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaStatementsService.hydrate(engagementId, reportId);
      const fresh = r.data ?? null;
      setData(fresh);
      return fresh;
    } catch (err) {
      setError(err as Error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [engagementId, reportId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};
